import {FormState} from '../../models/form-state.model';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {UserService} from '../../services/user.service';
import {PasswordChangeActions} from './password-change.actions';
import {clearFormValidationErrors, setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {PasswordPolicy} from '../../models/password-policy.model';
import {PasswordPolicyService} from '../../services/password-policy.service';
import {AuthService} from '../../services/auth.service';
import {FormStatus} from '../../enums/file-status.enum';
import {catchError, map} from 'rxjs/operators';

interface PasswordChangeStateModel {
    form: FormState;
    modalOpen: boolean;
    passwordPolicy: PasswordPolicy;
}

const STATE_NAME = 'passwordChange';

@State<PasswordChangeStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        modalOpen: false,
        passwordPolicy: null,
    }
})

@Injectable()
export class PasswordChangeState {
    constructor(
        private store: Store,
        private userService: UserService,
        private authService: AuthService,
        private passwordPolicyService: PasswordPolicyService,
    ) {
    }

    @Selector()
    static passwordChangeFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static modalVisibility(state) {
        return state.modalOpen;
    }

    @Selector()
    static passwordPolicy(state) {
        return state.passwordPolicy;
    }

    @Selector()
    static formSuccessful(state) {
        return state?.form?.status?.toString() === FormStatus.SUCCESS.toString();
    }

    @Action(PasswordChangeActions.FormFailure)
    changePasswordFailure(ctx: StateContext<PasswordChangeStateModel>, {validationErrors = []}: PasswordChangeActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(PasswordChangeActions.CloseModal)
    setModalVisibility(ctx: StateContext<PasswordChangeStateModel>) {
        clearFormValidationErrors(ctx);
        ctx.patchState({
            modalOpen: false,
        });
    }

    @Action(PasswordChangeActions.OpenModal)
    getPasswordPolicy(ctx: StateContext<PasswordChangeStateModel>) {
        return this.passwordPolicyService.fetchForCurrentUser().subscribe(
            passwordPolicy => {
                ctx.patchState({passwordPolicy, modalOpen: true});
            }
        );
    }

    @Action(PasswordChangeActions.Submit)
    submitPasswordChange(ctx: StateContext<PasswordChangeStateModel>) {
        const formData = ctx.getState()?.form?.model;
        return this.userService.passwordChange(formData).subscribe(
            () => {
                this.setModalVisibility(ctx);
            },
            ({error}) => ctx.dispatch(new PasswordChangeActions.FormFailure(error))
        );
    }

    @Action(PasswordChangeActions.OldPasswordReset)
    oldPasswordReset(ctx: StateContext<PasswordChangeStateModel>, {token}: PasswordChangeActions.OldPasswordReset) {
        const formData = ctx.getState()?.form?.model;
        return this.authService.oldPasswordReset(token, formData).pipe(
            map(() => {
                clearFormValidationErrors(ctx);
                return setFormStatus(ctx, FormStatus.SUCCESS, STATE_NAME);
            }),
            catchError(({error}) => {
                return ctx.dispatch(new PasswordChangeActions.FormFailure(error));
            })
        );
    }

}
